import SumoDidiProject from "../components/SumoDidiProject";
import SumoDidiMedia, { SumoDidiMediaLinks } from "../components/SumoDidiMedia";
import { withSumoDidiUpdates } from "../data/sumoDidiMedia";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { allWork } from "../data/mock";
import { ArrowLeft, ExternalLink } from "lucide-react";

const ProjectPage = () => {
  const { id } = useParams();
  const film = withSumoDidiUpdates(allWork.find((f) => f.id === id));

  // Dynamically inject the four new stills right after the first two existing stills
  const stillsToDisplay = film && film.id === "sumo-didi" && film.stills
    ? [
        ...film.stills.slice(0, 2),
        "/SUMO DIDI IMAGES 02_00126807.jpeg",
        "/SUMO DIDI IMAGES 04_00144670.jpeg",
        "/SUMO DIDI IMAGES 13_00144022.jpeg",
        "/VS--SDFORPREVIEW27MAYonVimeo-82’33”-2.jpg",
        ...film.stills.slice(2)
      ]
    : film ? film.stills : [];

  if (!film) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-[24px] text-[#1a1a1a]">Project not found</p>
          <Link
            to="/work"
            className="text-[12px] tracking-[0.15em] uppercase text-[#999] mt-6 inline-block hover:text-[#1a1a1a] transition-colors duration-500"
          >
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  if (film.id === "sumo-didi") {
    return <SumoDidiProject film={film} stills={stillsToDisplay} />;
  }

  return (
    <div className="bg-white min-h-screen pt-28 md:pt-36">
      {/* Hero — Vimeo player if embed exists, else hero image */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative w-full overflow-hidden bg-[#0a0a0a]"
      >
        <div className="max-w-[1400px] mx-auto">
          {film.embed ? (
            <div className="aspect-video w-full">
              <iframe
                src={film.embed}
                title={film.title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          ) : (
            <img
              src={film.hero}
              alt={film.title}
              className="w-full h-auto max-h-[80vh] object-contain mx-auto"
            />
          )}
        </div>
      </motion.section>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Back link + Title */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="pt-10"
        >
          <Link
            to="/work"
            className="group inline-flex items-center gap-2 text-[12px] tracking-[0.12em] uppercase text-[#999] hover:text-[#1a1a1a] transition-colors duration-500"
          >
            <ArrowLeft
              size={14}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            All Work
          </Link>

          {/* Title + laurels layout container placed right next to each other */}
          <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-16 mt-8">
            <div className="flex-shrink-0">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="font-serif text-[32px] md:text-[48px] lg:text-[56px] text-[#1a1a1a] tracking-[-0.02em]"
              >
                {film.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-[12px] text-[#999] tracking-[0.12em] uppercase mt-3"
              >
                {film.category + (film.duration ? " · " + film.duration : "") + (film.id === "sumo-didi" && film.year ? " · " + film.year : "")}
              </motion.p>
            </div>

            {/* Festival laurels conditionally displayed only for Sumo Didi */}
            {film.id === "sumo-didi" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="w-full md:w-auto max-w-[320px] md:max-w-[380px]"
              >
                <img 
                  src="/laurels_updated 2.png" 
                  alt="Film Festival Laurels" 
                  className="w-full h-auto object-contain"
                />
              </motion.div>
            )}
          </div>
        </motion.div>

        {film.id === "sumo-didi" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="pt-12 md:pt-16 max-w-[720px] w-full"
          >
            <SumoDidiMedia />
            <div className="mt-6 flex flex-col items-start gap-5"><SumoDidiMediaLinks /></div>
          </motion.div>
        )}

        {/* Logline & Director's Note */}
        <section className="py-16 md:py-24 max-w-[720px]">
          {film.logline && film.id === "sumo-didi" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-6">
                Logline
              </p>
              <p className="font-serif text-[20px] md:text-[24px] text-[#1a1a1a] leading-[1.6] tracking-[0.01em] text-justify">
                {film.logline}
              </p>
            </motion.div>
          )}

          {film.directorsNote && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mt-16"
            >
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-6">
                Director's Note
              </p>
              <p className="text-[15px] text-[#4a4a4a] leading-[1.9] tracking-[0.01em] text-justify">
                {film.directorsNote}
              </p>
            </motion.div>
          )}

          {/* Credits */}
          {film.credits && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mt-16"
            >
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-6">
                Credits
              </p>
              <div className="space-y-3">
                {Object.entries(film.credits).map(([key, value]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:gap-4">
                    <span className="text-[12px] text-[#999] tracking-[0.06em] capitalize min-w-[140px]">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-[14px] text-[#3a3a3a] leading-[1.6]">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </section>

        {/* Festivals */}
        {film.festivals && film.festivals.length > 0 && (
          <section className="pb-16 md:pb-24">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-8">
              Festivals & Recognition
            </p>
            <div className="space-y-4">
              {film.festivals.map((fest, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b border-[#f0eeeb]"
                >
                  <span className="text-[13px] text-[#1a1a1a] tracking-[0.02em]">
                    {fest.name}
                  </span>
                  <span className="text-[11px] text-[#999] tracking-[0.06em]">
                    {fest.note}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* External Press Link */}
            {film.externalLink && (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8"
              >
                <a
                  href={film.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-[12px] tracking-[0.1em] uppercase text-[#999] hover:text-[#1a1a1a] transition-colors duration-500"
                >
                  Press Coverage
                  <ExternalLink size={12} className="transition-transform duration-500 group-hover:translate-x-0.5" />
                </a>
              </motion.div>
            )}
          </section>
        )}

        {/* Film Stills & Posters Grid */}
        {stillsToDisplay && stillsToDisplay.length > 0 && (
          <section className="pb-24 md:pb-32">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-10">
              Selected Stills
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {stillsToDisplay.map((still, i) => {
                // The first 6 items are the uniform stills; the remaining 3 are the original posters
                const isPoster = film.id === "sumo-didi" && i >= 6;

                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.08 }}
                    className={isPoster ? "w-full overflow-hidden bg-[#f5f4f2]" : "aspect-video w-full overflow-hidden bg-[#f5f4f2]"}
                  >
                    <img
                      src={still}
                      alt={`${film.title} item ${i + 1}`}
                      loading="lazy"
                      className={isPoster ? "w-full h-auto object-contain" : "w-full h-full object-cover"}
                    />
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProjectPage;